import { useState, useMemo, useEffect, useRef } from 'react';
import { 
  DollarSign, 
  Eye, 
  Edit2, 
  Trash2, 
  Download, 
  Search, 
  Filter, 
  X,
  Calendar,
  ShoppingBag,
  Tag,
  User,
  FileText,
  RefreshCw,
  Plus
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import useApi from "../../../hooks/useApi";
import useAuth from "../../../hooks/useAuth";
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import DetailGastoModal from './DetailGastoModal';
import EditGastoModal from './EditGastoModal';
import { Gasto } from '../../../hooks/useApi';
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/ui/table";
import Badge from "../../../components/ui/badge/Badge";
import Button from "../../../components/ui/button/Button";
import Input from "../../../components/form/input/InputField";
import Select from "../../../components/form/Select";
import { Modal } from "../../../components/ui/modal";
import ComponentCard from "../../../components/common/ComponentCard";

interface GastosListProps {
  gastos: Gasto[]; // Updated to use Gasto type
  onDataChange?: () => Promise<void>;
  onCreateClick?: () => void;
}

interface Filters {
  categoria: string;
  fechaInicio: string;
  fechaFin: string;
  proveedor: string;
  montoMin: string;
  montoMax: string;
  metodo: string;
}

// Helper functions for styling
const getCategoriaClasses = (categoria: string): string => {
  const categories: Record<string, string> = {
    transporte: 'bg-purple-100 text-purple-800 dark:bg-purple-800/20 dark:text-purple-500',
    suministros: 'bg-blue-100 text-blue-800 dark:bg-blue-800/20 dark:text-blue-500',
    comisiones: 'bg-amber-100 text-amber-800 dark:bg-amber-800/20 dark:text-amber-500',
    servicios: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-800/20 dark:text-emerald-500',
    equipos: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-800/20 dark:text-indigo-500',
    alquiler: 'bg-rose-100 text-rose-800 dark:bg-rose-800/20 dark:text-rose-500',
    otros: 'bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-500'
  };
  return categories[categoria] || categories.otros;
};

const getMetodoClasses = (metodo: string): string => {
  const methods: Record<string, string> = {
    efectivo: 'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-500',
    transferencia: 'bg-blue-100 text-blue-800 dark:bg-blue-800/20 dark:text-blue-500',
    tarjeta: 'bg-purple-100 text-purple-800 dark:bg-purple-800/20 dark:text-purple-500',
    cheque: 'bg-amber-100 text-amber-800 dark:bg-amber-800/20 dark:text-amber-500',
    otro: 'bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-500'
  };
  return methods[metodo] || methods.otro;
};

const GastosList = ({ gastos, onDataChange, onCreateClick }: GastosListProps): JSX.Element => {
  const { 
    formatCurrency,
    deleteGasto
  } = useApi();
  
  const { canDelete, canEdit, canCreate } = useAuth();
  
  // State management
  const [selectedGasto, setSelectedGasto] = useState<Gasto | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    categoria: '',
    fechaInicio: '',
    fechaFin: '',
    proveedor: '',
    montoMin: '',
    montoMax: '',
    metodo: ''
  });
  
  // Get unique providers for dropdown
  const uniqueProviders = useMemo(() => {
    const providers = new Set(gastos.map(gasto => gasto.proveedor));
    return Array.from(providers).sort();
  }, [gastos]);
  
  // Filter providers based on search
  const filteredProviders = useMemo(() => {
    if (!filters.proveedor) return [];
    return uniqueProviders.filter(provider => 
      provider.toLowerCase().includes(filters.proveedor.toLowerCase())
    );
  }, [uniqueProviders, filters.proveedor]);
  
  // Show provider dropdown state
  const [showProviderDropdown, setShowProviderDropdown] = useState(false);
  
  // Ref for the provider dropdown container
  const providerDropdownRef = useRef<HTMLDivElement>(null);
  
  // Effect to handle clicks outside the dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (providerDropdownRef.current && !providerDropdownRef.current.contains(event.target as Node)) {
        setShowProviderDropdown(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Action handlers
  const handleViewDetails = (gasto: Gasto) => {
    setSelectedGasto(gasto);
    setShowDetailModal(true);
  };

  const handleEditGasto = (gasto: Gasto) => {
    setSelectedGasto(gasto);
    setShowEditModal(true);
  };

  const handleDeleteConfirm = (gasto: Gasto) => {
    setSelectedGasto(gasto);
    setShowDeleteModal(true);
  };

  const handleDeleteGasto = async () => {
    try {
      setLoading(true);
      if (selectedGasto && selectedGasto.id) {
        await deleteGasto(selectedGasto.id);
        toast.success('Gasto eliminado correctamente');
        setShowDeleteModal(false);
        if (onDataChange) {
          await onDataChange();
        }
      } else {
        toast.error('Error: ID de gasto no válido');
      }
    } catch (error) {
      toast.error('Error al eliminar el gasto');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportGasto = async (gasto: Gasto) => {
    // TODO: Implement export functionality for individual expenses
    // try {
    //   setLoading(true);
    //   // Create PDF export using jsPDF
    //   const { jsPDF } = await import('jspdf');
    //   const doc = new jsPDF({
    //     orientation: 'portrait',
    //     unit: 'mm',
    //     format: 'a4'
    //   });
    //
    //   // Define document properties
    //   const pageWidth = doc.internal.pageSize.getWidth();
    //   const pageHeight = doc.internal.pageSize.getHeight();
    //   const margin = 15;
    //   const contentWidth = pageWidth - (margin * 2);
    //   
    //   // Add company info, logo, etc.
    //   // ... code for PDF generation ...
    //   
    //   doc.save(`Gasto_${gasto.id}.pdf`);
    //   toast.success('Gasto exportado correctamente');
    // } catch (error) {
    //   toast.error('Error al exportar el gasto');
    //   console.error(error);
    // } finally {
    //   setLoading(false);
    // }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleFilterChange = (value: string, name: string) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSelectProvider = (provider: string) => {
    setFilters(prev => ({ ...prev, proveedor: provider }));
    setShowProviderDropdown(false);
  };

  const clearFilters = () => {
    setFilters({
      categoria: '',
      fechaInicio: '',
      fechaFin: '',
      proveedor: '',
      montoMin: '',
      montoMax: '',
      metodo: ''
    });
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const formatMoneyValue = (value: number | undefined | null): string => {
    if (value === undefined || value === null) return '-';
    return formatCurrency(value);
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy');
    } catch (error) {
      return dateString;
    }
  };

  // Filter gastos array based on search text and filters
  const filteredGastos = gastos.filter(gasto => {
    // Filter by search text
    if (searchText && !gasto.descripcion.toLowerCase().includes(searchText.toLowerCase()) && 
        !gasto.proveedor.toLowerCase().includes(searchText.toLowerCase())) {
      return false;
    }
    
    // Apply filters
    if (filters.categoria && gasto.categoria !== filters.categoria) return false;
    if (filters.metodo && gasto.metodoPago !== filters.metodo) return false;
    if (filters.proveedor) {
      if (!gasto.proveedor.toLowerCase().includes(filters.proveedor.toLowerCase())) {
        return false;
      }
    }
    
    // Date filters
    if (filters.fechaInicio) {
      const fechaInicio = new Date(filters.fechaInicio);
      fechaInicio.setHours(0, 0, 0, 0);
      const gastoFecha = new Date(gasto.fecha);
      if (gastoFecha < fechaInicio) return false;
    }
    
    if (filters.fechaFin) {
      const fechaFin = new Date(filters.fechaFin);
      fechaFin.setHours(23, 59, 59, 999);
      const gastoFecha = new Date(gasto.fecha);
      if (gastoFecha > fechaFin) return false;
    }
    
    // Amount filters
    if (filters.montoMin && gasto.monto < parseFloat(filters.montoMin)) return false;
    if (filters.montoMax && gasto.monto > parseFloat(filters.montoMax)) return false;
    
    return true;
  });

  return (
    <div className="h-full">
      <div className="p-4 space-y-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Buscar por proveedor o descripción..."
              value={searchText}
              onChange={handleSearchChange}
              className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2.5 text-sm text-gray-900 dark:text-white dark:border-gray-600 dark:bg-gray-800 dark:placeholder-gray-400 focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleFilters}
              startIcon={<Filter className="w-4 h-4" />}
            >
              Filtros
            </Button>
            {canCreate && (
              <Button 
                variant="primary"
                size="sm"
                startIcon={<Plus className="w-4 h-4" />}
                onClick={onCreateClick}
              >
                Nuevo gasto
              </Button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800/70 dark:to-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 pb-3 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Filter className="w-4 h-4 text-primary-500" />
                  Filtros avanzados
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Filtra los gastos según los criterios que necesites
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearFilters}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
              >
                <X className="w-4 h-4 mr-1.5" />
                Limpiar filtros
              </Button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-4">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Tag className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                  Categoría
                </label>
                <Select
                  options={[
                    { value: '', label: 'Todas las categorías' },
                    { value: 'transporte', label: 'Transporte' },
                    { value: 'suministros', label: 'Suministros' },
                    { value: 'comisiones', label: 'Comisiones' },
                    { value: 'servicios', label: 'Servicios' },
                    { value: 'equipos', label: 'Equipos' },
                    { value: 'alquiler', label: 'Alquiler' },
                    { value: 'otros', label: 'Otros' }
                  ]}
                  defaultValue={filters.categoria}
                  onChange={(value) => handleFilterChange(value, 'categoria')}
                  placeholder="Seleccionar categoría"
                  className="w-full"
                />
              </div>
              
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Calendar className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                  Desde
                </label>
                <Input
                  type="date"
                  value={filters.fechaInicio}
                  onChange={(e) => handleFilterChange(e.target.value, 'fechaInicio')}
                  onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.showPicker()}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Calendar className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                  Hasta
                </label>
                <Input
                  type="date"
                  value={filters.fechaFin}
                  onChange={(e) => handleFilterChange(e.target.value, 'fechaFin')}
                  onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.showPicker()}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <User className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                  Proveedor
                </label>
                <div className="relative" ref={providerDropdownRef}>
                  <Input
                    type="text"
                    value={filters.proveedor}
                    onChange={(e) => {
                      handleFilterChange(e.target.value, 'proveedor');
                      setShowProviderDropdown(true);
                    }}
                    onFocus={() => setShowProviderDropdown(true)}
                    placeholder="Nombre del proveedor"
                    className="w-full"
                  />
                  {filters.proveedor && showProviderDropdown && filteredProviders.length > 0 && (
                    <div 
                      className="absolute z-30 w-full mt-1 bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 rounded-md max-h-60 overflow-y-auto"
                    >
                      {filteredProviders.map((provider) => (
                        <div
                          key={provider}
                          className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSelectProvider(provider);
                          }}
                        >
                          <div className="flex items-center">
                            <User className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
                            <span className="text-gray-800 dark:text-gray-200">{provider}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <DollarSign className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                  Importe mínimo
                </label>
                <div className="relative">
                  <Input
                    type="number"
                    value={filters.montoMin}
                    onChange={(e) => handleFilterChange(e.target.value, 'montoMin')}
                    placeholder="0.00"
                    className="w-full"
                  />
                </div>
              </div>
              
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <DollarSign className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                  Importe máximo
                </label>
                <div className="relative">
                  <Input
                    type="number"
                    value={filters.montoMax}
                    onChange={(e) => handleFilterChange(e.target.value, 'montoMax')}
                    placeholder="0.00"
                    className="w-full"
                  />
                </div>
              </div>
              
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <FileText className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                  Método de pago
                </label>
                <Select
                  options={[
                    { value: '', label: 'Todos los métodos' },
                    { value: 'efectivo', label: 'Efectivo' },
                    { value: 'transferencia', label: 'Transferencia' },
                    { value: 'tarjeta', label: 'Tarjeta' },
                    { value: 'cheque', label: 'Cheque' },
                    { value: 'otro', label: 'Otro' }
                  ]}
                  defaultValue={filters.metodo}
                  onChange={(value) => handleFilterChange(value, 'metodo')}
                  placeholder="Seleccionar método"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        )}

        <div className="scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent dark:scrollbar-track-gray-800/50 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <TableHeader className="bg-gray-50 dark:bg-gray-800/50">
              <TableRow>
                <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Fecha
                </TableCell>
                <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Proveedor
                </TableCell>
                <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Descripción
                </TableCell>
                <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Categoría
                </TableCell>
                <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Método de Pago
                </TableCell>
                <TableCell isHeader className="px-6 py-3.5 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Monto
                </TableCell>
                <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-gray-700">
              {filteredGastos.length > 0 ? (
                filteredGastos.map((gasto) => (
                  <TableRow 
                    key={gasto.id} 
                    className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 whitespace-nowrap">
                      {formatDate(gasto.fecha)}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                      <div className="flex items-center space-x-2">
                        <span className="truncate max-w-[150px]" title={gasto.proveedor}>
                          {gasto.proveedor}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                      <div className="truncate max-w-[200px]" title={gasto.descripcion}>
                        {gasto.descripcion}
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getCategoriaClasses(gasto.categoria)}`}>
                        {gasto.categoria.charAt(0).toUpperCase() + gasto.categoria.slice(1)}
                      </span>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getMetodoClasses(gasto.metodoPago)}`}>
                        {gasto.metodoPago.charAt(0).toUpperCase() + gasto.metodoPago.slice(1)}
                      </span>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 text-right whitespace-nowrap font-medium">
                      {formatMoneyValue(gasto.monto)}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm">
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleViewDetails(gasto)}
                          aria-label="Ver detalles"
                          className="text-gray-500 hover:text-gray-700 hover:border-gray-500"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        
                        {canEdit && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleEditGasto(gasto)}
                            aria-label="Editar gasto"
                            className="text-blue-500 hover:text-blue-700 hover:border-blue-500"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {canDelete && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleDeleteConfirm(gasto)}
                            aria-label="Eliminar gasto"
                            className="text-red-500 hover:text-red-700 hover:border-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    {searchText || Object.values(filters).some(f => f !== '') ? (
                      <div>
                        <p>No se encontraron gastos con los filtros aplicados.</p>
                      </div>
                    ) : (
                      <p>No hay gastos registrados en el sistema.</p>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirmación de eliminación"
        size="md"
      >
        <div className="space-y-6 px-2">
          <div className="space-y-4">
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
              ¿Está seguro de eliminar este gasto?
            </p>
            
            <div className="space-y-3">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción eliminará permanentemente todos los datos asociados al gasto, incluyendo:
              </p>
              
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 ml-4 space-y-1">
                <li>Información del gasto</li>
                <li>Registros de transacciones</li>
                <li>Historial financiero relacionado</li>
                <li>Cualquier otro dato asociado</li>
              </ul>
            </div>

            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-lg">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleDeleteGasto}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? 'Eliminando...' : 'Sí, eliminar gasto'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Detail Modal */}
      {selectedGasto && showDetailModal && (
        <DetailGastoModal
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          gasto={selectedGasto}
        />
      )}

      {/* Edit Modal */}
      {selectedGasto && showEditModal && (
        <EditGastoModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          gasto={selectedGasto}
          onSuccess={async () => {
            setShowEditModal(false);
            if (onDataChange) {
              await onDataChange();
            }
          }}
        />
      )}
    </div>
  );
};

export default GastosList; 